/**
 * Map of events to their inferred prior state.
 * If the first event in a range is X, the state before it was Y.
 */
const INFERRED_PRIOR_STATE = {
  "Charging Started": "EV Plugged In",
  "Charging Stopped": "Charging Started",
  "EV Plugged Out": "EV Plugged In",
  "EVSE Not Ready": "EV Plugged In",
  "EV Not Ready": "EV Plugged In",
  "Fault Cleared": "Fault Detected",
  "Gateway Connected": "Gateway Disconnected",
  "Safe Limit Mode Cleared": "Safe Limit Mode",
};

/**
 * For stateful events, the API sends state: "SET" or "CLR".
 * When "CLR", we map the eventName to a "cleared" equivalent for display.
 */
const STATEFUL_CLR_MAP = {
  "Gateway Disconnected": "Gateway Connected",
  "Safe Limit Mode": "Safe Limit Mode Cleared",
  // All faults map to a generic "Fault Cleared"
  "Ground Missing Fault": "Fault Cleared",
  "Vehicle Fault": "Fault Cleared",
  "CCID Fault": "Fault Cleared",
  "Contactor Bad Fault": "Fault Cleared",
  "Internal Error": "Fault Cleared",
  "Cosmos Communication Fault": "Fault Cleared",
  "Overcurrent Fault": "Fault Cleared",
  "Connector Lock Failure": "Fault Cleared",
  "System Off Grid": "Fault Cleared",
};

/**
 * Normalizes an event's eventName based on its state field.
 * For stateful events with state: "CLR", maps to the cleared equivalent.
 */
export function normalizeEventName(event) {
  if (event.state === "CLR" && STATEFUL_CLR_MAP[event.eventName]) {
    return STATEFUL_CLR_MAP[event.eventName];
  }
  return event.eventName;
}

/**
 * Converts an array of raw events into timeline segments using the actual
 * eventName and date from the API. Each segment runs from one event to the next.
 *
 * @param {Array<{eventName: string, date: string}>} events
 * @param {[number, number]} dateRange - [startMs, endMs]
 * @returns {Array<{label: string, start: number, end: number, durationMs: number}>}
 */
export function eventsToSegments(events, dateRange) {
  if (!events || events.length === 0) return [];

  const [rangeStart, rangeEnd] = dateRange;

  // Sort oldest-first by date, normalize stateful event names
  const sorted = [...events]
    .map(e => ({ ...e, ts: new Date(e.date).getTime(), normalizedName: normalizeEventName(e) }))
    .sort((a, b) => a.ts - b.ts);

  const segments = [];

  // If the first event starts after the range start, infer the prior state from
  // what the first event transitions away from.
  if (sorted.length > 0 && sorted[0].ts > rangeStart) {
    const firstEvent = sorted[0].normalizedName;
    const preLabel = INFERRED_PRIOR_STATE[firstEvent] || "EV Plugged Out";
    const gapEnd = sorted[0].ts;
    segments.push({
      label: preLabel,
      start: rangeStart,
      end: gapEnd,
      durationMs: gapEnd - rangeStart,
    });
  }

  sorted.forEach((ev, i) => {
    const start = ev.ts;
    const end = i < sorted.length - 1 ? sorted[i + 1].ts : rangeEnd;

    segments.push({
      label: ev.normalizedName,
      start,
      end: Math.max(end, start),
      durationMs: Math.max(end - start, 0),
    });
  });

  return segments;
}

/**
 * Generates segments for a specific timeline track.
 * Filters events to only those belonging to the track, then builds segments.
 *
 * @param {Array<{eventName: string, date: string}>} events - All events
 * @param {[number, number]} dateRange - [startMs, endMs]
 * @param {object} track - Track definition from TIMELINE_TRACKS
 * @returns {Array<{label: string, start: number, end: number, durationMs: number}>}
 */
export function eventsToTrackSegments(events, dateRange, track) {
  if (!events || events.length === 0) return [];

  const [rangeStart, rangeEnd] = dateRange;
  const trackEventSet = new Set(track.events);

  // Normalize event names (handle SET/CLR state) and filter to this track
  const trackEvents = events
    .map(e => ({ ...e, normalizedName: normalizeEventName(e) }))
    .filter(e => trackEventSet.has(e.normalizedName));
  if (trackEvents.length === 0) return [];

  // Sort oldest-first
  const sorted = [...trackEvents].map(e => ({ ...e, ts: new Date(e.date).getTime() })).sort((a, b) => a.ts - b.ts);

  const segments = [];

  // Infer state before the first event
  if (sorted[0].ts > rangeStart) {
    const firstEvent = sorted[0].normalizedName;
    const preLabel = INFERRED_PRIOR_STATE[firstEvent] || track.defaultState;
    segments.push({
      label: preLabel,
      start: rangeStart,
      end: sorted[0].ts,
      durationMs: sorted[0].ts - rangeStart,
    });
  }

  sorted.forEach((ev, i) => {
    const start = ev.ts;
    const end = i < sorted.length - 1 ? sorted[i + 1].ts : rangeEnd;

    segments.push({
      label: ev.normalizedName,
      start,
      end: Math.max(end, start),
      durationMs: Math.max(end - start, 0),
    });
  });

  return segments;
}

/**
 * Formats a duration in ms to a human-readable string like "5h07m" or "2m".
 * @param {number} ms
 * @returns {string}
 */
export function formatDuration(ms) {
  const totalMin = Math.floor(ms / 60000);
  const hours = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  if (hours > 0) {
    return `${hours}h${String(mins).padStart(2, "0")}m`;
  }
  return `${mins}m`;
}
